// Padding that holds a page's *content* clear of the hardware intrusions the
// screen reports — a notch or punch-hole camera, which lands on a side edge
// once the phone is turned landscape, and the home-indicator strip along the
// bottom. The numbers come from the --safe-* variables in App.css: 0 on any
// screen without a cutout, with an 8px floor at phone widths for the rounded
// corners the insets know nothing about.
//
// The page element itself keeps spanning the whole screen, so a background
// image on it still reaches into those areas and the insets read as wallpaper
// margin rather than as letterboxing. That is the same split the game screen
// makes — see the padding on the root in Pages/Game/index.tsx.
//
// Three rules for anything added here:
//
//  * `max(gutter, inset)` rather than gutter + inset, so a screen with no
//    cutout keeps exactly the padding the page was designed with and a phone
//    gains only the pixels it actually needs. Worth noting the gutters are
//    mostly rem, and index.css halves the root font size at phone widths — a
//    `px-6` that reads as 24px on a desktop is 12px on a phone, i.e. well
//    inside a 44px notch. The insets are what stop that.
//  * The safe padding *replaces* the gutter utility it came from instead of
//    sitting beside it. Tailwind emits every `sm:` rule after every unprefixed
//    one, so a leftover `sm:px-3` would win over an unprefixed
//    `pl-[max(...)]` on exactly the screens this exists for.
//  * Written out as literal strings. Tailwind scans source text for class
//    names, so an arbitrary value assembled at runtime is never emitted.

/**
 * The content frame shared by the lobby-family pages (Challenges, Stats,
 * Profile Settings): the 1.5rem / 0.75rem gutter they all had, with each side
 * held off its own inset.
 *
 * Goes on the scrolling body under the Navbar, never on the page root — the
 * root has to stay full-bleed for the background, and the Navbar is a
 * full-width band that pads its own controls off the side insets.
 */
export const SAFE_PAGE_FRAME =
  "pt-6 pr-[max(1.5rem,var(--safe-right))] pb-[max(1.5rem,var(--safe-bottom))] pl-[max(1.5rem,var(--safe-left))] " +
  "sm:pt-3 sm:pr-[max(0.75rem,var(--safe-right))] sm:pb-[max(0.75rem,var(--safe-bottom))] sm:pl-[max(0.75rem,var(--safe-left))]"

/**
 * A full-screen overlay's own padding. The scrim still covers the whole
 * screen — only the card floating in it is held out of the cutouts.
 */
export const SAFE_OVERLAY_PADDING =
  "p-4 pt-[max(1rem,var(--safe-top))] pr-[max(1rem,var(--safe-right))] pb-[max(1rem,var(--safe-bottom))] pl-[max(1rem,var(--safe-left))]"

/** The short-screen tier of {@link SAFE_OVERLAY_PADDING}; use the two together. */
export const SAFE_OVERLAY_PADDING_SM =
  "sm:p-[10px] sm:pt-[max(10px,var(--safe-top))] sm:pr-[max(10px,var(--safe-right))] sm:pb-[max(10px,var(--safe-bottom))] sm:pl-[max(10px,var(--safe-left))]"
